// Library Methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Styles
import {
  getSurfaceTokens,
  noHoverBackgroundSx,
} from "src/styles/mui/muiStylesHelper";

// Types
import { UpdateClinicSettingsType } from "src/services/clinics/types";

type ReportOrderRadioType = {
  isDisabled: boolean;
  currentSettings: UpdateClinicSettingsType;
  handleUpdateSettings: (settings: UpdateClinicSettingsType) => void;
};

const ReportOrderRadio = ({
  isDisabled,
  currentSettings,
  handleUpdateSettings,
}: ReportOrderRadioType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText: mutedTextColor } = getSurfaceTokens(theme);

  const reportDisplayLeftEyeFirst = useMemo(
    () => Boolean(currentSettings?.reportDisplayLeftEyeFirst),
    [currentSettings?.reportDisplayLeftEyeFirst]
  );

  const handleUpdateRadio = useCallback(
    (inputValue: boolean) => {
      handleUpdateSettings({
        ...currentSettings,
        reportDisplayLeftEyeFirst: inputValue,
      });
    },
    [handleUpdateSettings, currentSettings]
  );

  return (
    <Grid>
      <Grid item display="flex" alignItems="center">
        <Radio
          disabled={isDisabled}
          checked={reportDisplayLeftEyeFirst}
          onChange={() => handleUpdateRadio(true)}
          name="radio-button-left"
          inputProps={{ "aria-label": "Left" }}
          sx={{
            boxShadow: "none",
            "&.MuiButtonBase-root.MuiRadio-root": {
              color: "#E2772E",
            },
            ...noHoverBackgroundSx,
          }}
        />
        <Typography fontSize="14px" color={mutedTextColor}>
          {t("Left Eye Displayed First")}
        </Typography>
      </Grid>
      <Grid item display="flex" alignItems="center">
        <Radio
          disabled={isDisabled}
          checked={!reportDisplayLeftEyeFirst}
          onChange={() => handleUpdateRadio(false)}
          name="radio-button-right"
          inputProps={{ "aria-label": "Right" }}
          sx={{
            boxShadow: "none",
            "&.MuiButtonBase-root.MuiRadio-root": {
              color: "#E2772E",
            },
            ...noHoverBackgroundSx,
          }}
        />
        <Typography fontSize="14px" color={mutedTextColor}>
          {t("Right Eye Displayed First")}{" "}
          <span style={{ fontWeight: 600 }}>{t("(Default)")}</span>
        </Typography>
      </Grid>
    </Grid>
  );
};

export default ReportOrderRadio;
